import { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Button, CheckBox } from "@etframework/ui.components";
import { useUpdateExtendedDate } from "../../data/mutations/taskMutations";
import { Trans, useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import { toast } from "react-toastify";
import useEvent from "../../hooks/useEvent";
import { useQueryClient } from "@tanstack/react-query";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";

export const UndoExtendedDateModal = () => {
  const revertDueDateExtensionRef = useRef(null);
  const [task, setTask] = useState(false);

  const queryClient = useQueryClient();
  const { Events, toggleEvent } = useEvent();
  const { t } = useTranslation(["Overlay"], {
    useSuspense: false,
  });

  const { UserId } = useDueDatesAuth();

  const { mutate: updateExtendedDate, isPending } = useUpdateExtendedDate();

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const closeModal = () => {
    setShowModal(false);
    toggleEvent(Events.ClosingExtendedDateModal);
  };

  const openModal = () => {
    setShowModal(true);
    modalRef?.current?.open();
  };

  const toggleModal = (event) => {
    setTask(event.detail.task);

    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    const undoExtendedDate = async () => {
      const postData = {
        Id: task?.Id,
        ExtendedDate: null,
        ApplyToDueDate: revertDueDateExtensionRef.current?.checked,
        CurrentUserId: UserId,
      };

      await updateExtendedDate(
        { task: postData },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(["get-task", task?.Id]);
            toggleEvent(Events.RefreshTaskGrid);
            toast.success(t("Success"), {
              position: "bottom-left",
            });
            closeModal();
          },
          onError: () => {
            toast.error(t("ErrorMsg"), {
              position: "bottom-left",
            });
          },
        }
      );
    };

    return (
      <div className="et-extended-duedate-modal">
        <div className="my-4 fs-16">
          {t("UndoExtendedDateModal.UndoExtension")}
        </div>

        <div>
          <Trans components={{ b: <strong /> }}>
            {t("UndoExtendedDateModal.RemovingExtension")}
          </Trans>
          <div className="my-4">
            <CheckBox
              ref={revertDueDateExtensionRef}
              name="UndoExtensionInDueDate"
              id={ModalEvents.UndoExtendedDateModal.Id + "_undo-extension"}
              label={t("UndoExtendedDateModal.RevertTaskDueDate")}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-3 mt-5">
          <Button
            text={t("Cancel")}
            className="btn-tertiary w-auto"
            onClick={closeModal}
          />
          <Button
            id={ModalEvents.UndoExtendedDateModal.Id + "_apply-button"}
            text={t("ExtendDateModal.Confirm")}
            className="btn btn-primary w-auto"
            onClick={undoExtendedDate}
            loading={isPending}
          />
        </div>
      </div>
    );
  };

  useRegisterEvent(ModalEvents.UndoExtendedDateModal, toggleModal);

  return ReactDOM.createPortal(
    <>
      <Modal
        id={ModalEvents.UndoExtendedDateModal.Id}
        ref={modalRef}
        isModal
        showCloseIcon={true}
        visible={showModal}
        content={<ModalContent />}
        close={closeModal}
        minHeight="20vh"
        width="500px"
      ></Modal>
    </>,
    document.body
  );
};
